import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { confirmDocumentFact, documentFactsSummary, listDocumentFacts, rejectDocumentFact } from "@hp/api-client";
import type { DocumentFact } from "@hp/api-client";
import { useAuth } from "../auth/AuthContext";
import { SourceDocumentViewer } from "../components/SourceDocumentViewer";
import { PageHeader } from "../components/layout/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "../components/Feedback";

type Status = "needs_review" | "auto" | "confirmed" | "rejected";

const STATUSES: { key: Status; label: string }[] = [
  { key: "needs_review", label: "To check" },
  { key: "auto", label: "Not yet confirmed" },
  { key: "confirmed", label: "Confirmed" },
  { key: "rejected", label: "Rejected" },
];

const TONES: Record<Status, string> = {
  needs_review: "bg-amber-50 text-amber-800",
  auto: "bg-zinc-100 text-zinc-700",
  confirmed: "bg-emerald-50 text-emerald-800",
  rejected: "bg-rose-50 text-rose-800",
};

const TYPE_LABELS: Record<string, string> = {
  party: "Party",
  capacity: "Capacity",
  term: "Term",
  payment_security: "Payment security",
  remedy: "Remedy",
  charge: "Charge",
  escalation: "Escalation",
  quantity: "Quantity",
  link: "Link",
  other: "Other",
};

function Fact({ fact, onChanged }: { fact: DocumentFact; onChanged: () => void }) {
  const [editing, setEditing] = React.useState(false);
  const [viewing, setViewing] = React.useState(false);
  const [value, setValue] = React.useState(fact.value);
  const confirm = useMutation({ mutationFn: () => confirmDocumentFact(fact.id, value !== fact.value ? { value } : {}), onSuccess: () => { setEditing(false); onChanged(); } });
  const reject = useMutation({ mutationFn: () => rejectDocumentFact(fact.id), onSuccess: onChanged });
  const failed = confirm.error ?? reject.error;
  return (
    <li className="rounded-md border border-zinc-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={["rounded px-1.5 py-0.5 text-xs font-medium", TONES[fact.status]].join(" ")}>{STATUSES.find((s) => s.key === fact.status)?.label}</span>
        <span className="rounded bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-800">{TYPE_LABELS[fact.fact_type] ?? fact.fact_type}</span>
        <span className="text-xs text-zinc-600">{fact.contract || "Document"}</span>
        {fact.effective_from ? <span className="text-xs text-zinc-500">from {fact.effective_from}</span> : null}
        <span className="ml-auto text-xs tabular-nums text-zinc-500">{Math.round(fact.confidence * 100)}% sure</span>
      </div>
      <p className="mt-2 font-medium text-zinc-900">{fact.label || fact.subject}</p>
      {editing ? (
        <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3} className="mt-1 w-full rounded-md border border-zinc-300 p-2 text-sm" aria-label="The fact, as you want it kept" />
      ) : (
        <p className="mt-1 whitespace-pre-wrap text-zinc-800">{fact.value}</p>
      )}
      {fact.review_note ? <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">{fact.review_note}</p> : null}
      <blockquote className="mt-2 border-l-2 border-zinc-200 pl-2 text-xs text-zinc-600">{fact.quote}</blockquote>
      <p className="mt-1 text-xs text-zinc-500">
        {fact.file_name}
        {fact.clause_ref ? ` · ${fact.clause_ref}` : ""}
        {fact.page ? ` · page ${fact.page}` : ""}
        {fact.confirmed_by ? ` · ${fact.status} by ${fact.confirmed_by}` : ""}
      </p>
      <button type="button" onClick={() => setViewing(true)} className="mt-1 text-xs font-medium text-sky-700 underline underline-offset-2 hover:text-sky-900">
        Open the PDF{fact.page ? ` at page ${fact.page}` : ""}
      </button>
      {viewing ? (
        <SourceDocumentViewer
          source={{ type: "source_document", id: fact.source_document_id, label: fact.file_name }}
          passage={fact.page && fact.quote ? { text: fact.quote, page: fact.page } : undefined}
          startPage={fact.page}
          onClose={() => setViewing(false)}
        />
      ) : null}
      {failed ? <p role="alert" className="mt-2 text-xs text-rose-700">That did not save. Try again.</p> : null}
      {fact.status !== "rejected" ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={() => confirm.mutate()} disabled={confirm.isPending} className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50">
            <Check className="h-3.5 w-3.5" aria-hidden /> {editing ? "Save and confirm" : fact.status === "confirmed" ? "Confirmed" : "Confirm"}
          </button>
          <button type="button" onClick={() => setEditing((v) => !v)} className="rounded-md border border-zinc-300 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50">
            {editing ? "Cancel" : "Correct"}
          </button>
          <button type="button" onClick={() => reject.mutate()} disabled={reject.isPending} className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50">
            <X className="h-3.5 w-3.5" aria-hidden /> Reject
          </button>
        </div>
      ) : null}
    </li>
  );
}

export function DocumentFactsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<Status>("needs_review");
  const [needle, setNeedle] = React.useState("");
  const facts = useQuery({ queryKey: ["document-facts", status, needle], queryFn: () => listDocumentFacts({ status, q: needle || undefined, limit: 300 }), enabled: isAdmin });
  const summary = useQuery({ queryKey: ["document-facts-summary"], queryFn: documentFactsSummary, enabled: isAdmin });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["document-facts"] });
    void queryClient.invalidateQueries({ queryKey: ["document-facts-summary"] });
  };

  if (!isAdmin) {
    return (
      <div className="page-stack">
        <PageHeader title="Document facts" description="Only an admin can check the facts read from the documents." />
      </div>
    );
  }
  return (
    <div className="page-stack">
      <PageHeader
        title="Document facts"
        description="The parties, capacities, payment security, charges, escalation and coal quantities read from each agreement, each with the page and words it came from. A fact you confirm is given to HP Genie as checked; one you reject is never used."
      />
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Which facts" className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={status === s.key}
              onClick={() => setStatus(s.key)}
              className={["rounded-full border px-3 py-1 text-xs font-medium", status === s.key ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"].join(" ")}
            >
              {s.label}
              {summary.data ? <span className="ml-1 tabular-nums opacity-70">{summary.data[s.key] ?? 0}</span> : null}
            </button>
          ))}
        </div>
        <label className="relative ml-auto block">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" aria-hidden />
          <input type="search" value={needle} onChange={(e) => setNeedle(e.target.value)} placeholder="Find a fact" className="rounded-md border border-zinc-200 py-1 pl-7 pr-2 text-xs" />
        </label>
      </div>
      {facts.isLoading ? <LoadingState label="Loading the facts..." /> : null}
      {facts.error ? <ErrorState error={facts.error} label="The facts could not be loaded" /> : null}
      {facts.data && facts.data.length === 0 ? <EmptyState>No facts here.</EmptyState> : null}
      {facts.data?.length ? (
        <ul className="space-y-2">
          {facts.data.map((f) => (
            <Fact key={f.id} fact={f} onChanged={refresh} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
